import { usePage } from '@inertiajs/react'
import { useEffect } from 'react'

/**
 * Track and recall the full URL of the last visit to a named screen, so that
 * "Back to X" links from detail pages can return the user to the same filter
 * / sort / search state they had on the index.
 *
 * Scoped to sessionStorage — resets when the tab closes, doesn't leak across
 * sessions or users.
 */

const KEY = (name: string) => `rememberedUrl:${name}`

export function rememberUrl(name: string, url: string) {
  try {
    window.sessionStorage.setItem(KEY(name), url)
  } catch {
    /* ignore quota / disabled-storage errors */
  }
}

export function getRememberedUrl(name: string, fallback: string): string {
  if (typeof window === 'undefined') return fallback
  try {
    return window.sessionStorage.getItem(KEY(name)) ?? fallback
  } catch {
    return fallback
  }
}

/**
 * Call at the top of an index page component. Continuously syncs the
 * current URL (including query string) into sessionStorage so detail
 * pages can read it back.
 */
export function useRememberThisUrl(name: string) {
  const { url } = usePage()
  useEffect(() => {
    rememberUrl(name, url)
  }, [name, url])
}
